/**
 * Pure logic for supplier returns (CC-6: NEVER use lingui macros here).
 *
 * A supplier return (`purchase_return`) is an INVERSE picking against an
 * already received purchase order: it removes stock from the warehouse (an
 * outgoing `stock_move`) and records a NEGATIVE valuation layer at the current
 * moving-average cost, just like a normal delivery but documented as a return.
 *
 * Quantities are INTEGERS (minor units, factor 1000), just like F1–F4.
 */

export type PurchaseReturnState = "draft" | "done";

export const PURCHASE_RETURN_STATES: readonly PurchaseReturnState[] = ["draft", "done"] as const;

export function isPurchaseReturnState(value: string): value is PurchaseReturnState {
  return (PURCHASE_RETURN_STATES as readonly string[]).includes(value);
}

export function coercePurchaseReturnState(value: string | null | undefined): PurchaseReturnState {
  return value && isPurchaseReturnState(value) ? value : "draft";
}

/** Can the return be confirmed? Only from draft. */
export function canConfirmReturn(state: string | null | undefined): boolean {
  return coercePurchaseReturnState(state) === "draft";
}

/** Return line (relevant subset for the calculation). */
export interface ReturnLineLike {
  product_id: string | null;
  /** Quantity to return (minor units). */
  qty: number | null;
}

/** Planned output of a return (product × quantity). */
export interface ReturnEffect {
  productId: string;
  /** Quantity to remove from on-hand (minor units, > 0). */
  qty: number;
}

/**
 * Plans the effects of a return: one output per line with quantity > 0.
 * Aggregates by product (several lines of the same product are added). Ignores lines
 * without a product or with quantity <= 0. Deterministic, side-effect free.
 */
export function planReturnEffects(lines: ReturnLineLike[]): ReturnEffect[] {
  const byProduct = new Map<string, number>();
  for (const line of lines) {
    const productId = line.product_id;
    const qty = line.qty ?? 0;
    if (!productId || qty <= 0) {
      continue;
    }
    byProduct.set(productId, (byProduct.get(productId) ?? 0) + qty);
  }
  return [...byProduct.entries()].map(([productId, qty]) => ({ productId, qty }));
}

/** Total units to return from a return (minor units). */
export function totalReturnQty(lines: ReturnLineLike[]): number {
  return lines.reduce((acc, l) => acc + Math.max(l.qty ?? 0, 0), 0);
}
